import React from 'react';
import PropTypes from 'prop-types';
import { LuCircleCheck, LuPlay, LuRotateCcw } from 'react-icons/lu';
import { useTranslation } from '../../../i18n/LanguageContext';
import { CHAPTER_ICONS, POPULAR_TOPICS } from './chapters';
import { TOPIC_KEYS, useGuideProgress } from './progress';
import { useGuideLength } from './useUserGuide';

// Steps of the opening; a position there is not worth "continuing"
const OPENING_STEPS = ['intro', 'howTo'];

const PRIMARY = 'tw-relative tw-inline-flex tw-items-center tw-justify-center tw-gap-2 tw-overflow-hidden tw-rounded-full tw-border tw-border-white/50 tw-bg-gradient-to-b tw-from-teal-400 tw-to-cyan-600 tw-px-5 tw-py-2.5 tw-text-sm tw-font-semibold tw-text-white tw-shadow-lg tw-shadow-teal-500/30 tw-transition-transform tw-duration-200 active:tw-scale-[0.97] focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/60 dark:tw-border-white/25';
const CHIP = 'tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-border tw-border-slate-200/80 tw-bg-white/80 tw-px-3 tw-py-1.5 tw-text-xs tw-font-semibold tw-text-slate-700 tw-transition-colors hover:tw-border-teal-400/60 hover:tw-bg-teal-50 hover:tw-text-teal-800 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50 dark:tw-border-white/10 dark:tw-bg-white/[0.05] dark:tw-text-slate-200 dark:hover:tw-bg-teal-500/10 dark:hover:tw-text-teal-100';

/**
 * Home-page entry to the new-system guide: play (or continue where the
 * viewer left off), jump straight to a popular topic, see what was watched.
 * `onOpen(start)` opens the player; start is null, { chapter } or { beat }.
 */
const UserGuideCard = ({ onOpen }) => {
  const { t } = useTranslation();
  const label = (key, vars) => t(`userGuide.card.${key}`, vars);
  const { minutes } = useGuideLength();
  const { watched, resume } = useGuideProgress();
  const done = TOPIC_KEYS.filter((key) => watched.includes(key)).length;
  const canResume = Boolean(resume) && !OPENING_STEPS.includes(resume);
  const startMain = () => onOpen(canResume ? { beat: resume } : null);

  return (
    <section aria-labelledby="user-guide-card-title" className="tw-relative tw-isolate tw-overflow-hidden tw-rounded-[1.75rem] tw-border tw-border-teal-500/20 tw-bg-white/75 tw-p-5 tw-shadow-[inset_0_1px_0_rgba(255,255,255,1),0_16px_40px_-28px_rgba(15,23,42,0.35)] sm:tw-p-6 dark:tw-border-teal-400/15 dark:tw-bg-[#22262f]/80">
      <span aria-hidden="true" className="tw-pointer-events-none tw-absolute -tw-left-20 -tw-top-24 -tw-z-10 tw-h-64 tw-w-64 tw-rounded-full tw-bg-teal-300/35 tw-blur-3xl dark:tw-bg-teal-500/15" />
      <span aria-hidden="true" className="tw-pointer-events-none tw-absolute -tw-bottom-28 tw-right-0 -tw-z-10 tw-h-64 tw-w-72 tw-rounded-full tw-bg-indigo-300/30 tw-blur-3xl dark:tw-bg-indigo-500/15" />

      <div className="tw-flex tw-flex-col tw-gap-4 md:tw-flex-row md:tw-items-center md:tw-gap-6">
        <button
          type="button"
          onClick={startMain}
          aria-label={canResume ? label('resume') : label('watch')}
          className="tw-group tw-relative tw-grid tw-h-36 tw-w-full tw-shrink-0 tw-place-items-center tw-overflow-hidden tw-rounded-2xl tw-bg-gradient-to-br tw-from-teal-500 tw-via-cyan-600 tw-to-indigo-600 tw-shadow-lg tw-shadow-teal-500/30 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/60 focus-visible:tw-ring-offset-2 md:tw-h-28 md:tw-w-44"
        >
          <span aria-hidden="true" className="tw-absolute tw-inset-0 tw-bg-[radial-gradient(120%_90%_at_20%_0%,rgba(255,255,255,0.35),rgba(255,255,255,0)_60%)]" />
          <span className="tw-relative tw-grid tw-h-14 tw-w-14 tw-place-items-center tw-rounded-full tw-bg-white tw-text-teal-600 tw-shadow-xl tw-transition-transform tw-duration-300 group-hover:tw-scale-110">
            <LuPlay aria-hidden="true" className="tw-ml-1 tw-h-6 tw-w-6" />
          </span>
          <span className="tw-absolute tw-bottom-2 tw-right-2.5 tw-rounded-full tw-bg-black/35 tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-semibold tw-tabular-nums tw-text-white">{t('userGuide.card.duration', { minutes })}</span>
        </button>

        <div className="tw-min-w-0 tw-flex-1">
          <p className="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-[0.12em] tw-text-teal-700 dark:tw-text-teal-300">{label('eyebrow', { minutes })}</p>
          <h2 id="user-guide-card-title" className="tw-mt-1 tw-text-lg tw-font-semibold tw-tracking-tight tw-text-slate-900 dark:tw-text-white">{label('title')}</h2>
          <p className="tw-mt-1 tw-text-sm tw-leading-relaxed tw-text-slate-600 dark:tw-text-slate-400">{label('text')}</p>
          <div className="tw-mt-3 tw-flex tw-flex-wrap tw-items-center tw-gap-1.5">
            <span className="tw-mr-1 tw-text-xs tw-font-medium tw-text-slate-500 dark:tw-text-slate-400">{label('topics')}:</span>
            {POPULAR_TOPICS.map((key) => {
              const Icon = CHAPTER_ICONS[key];
              return (
                <button key={key} type="button" onClick={() => onOpen({ chapter: key })} className={CHIP}>
                  <Icon aria-hidden="true" className="tw-h-3.5 tw-w-3.5 tw-text-teal-600 dark:tw-text-teal-300" />
                  {t(`userGuide.chapters.${key}`)}
                  {watched.includes(key) && <LuCircleCheck aria-label={t('userGuide.player.watched')} className="tw-h-3.5 tw-w-3.5 tw-text-emerald-500" />}
                </button>
              );
            })}
          </div>
        </div>

        <div className="tw-flex tw-shrink-0 tw-flex-col tw-gap-2 md:tw-w-56">
          <button type="button" onClick={startMain} className={PRIMARY}>
            <span aria-hidden="true" className="tw-pointer-events-none tw-absolute tw-inset-0 tw-rounded-[inherit] tw-bg-[radial-gradient(120%_90%_at_30%_0%,rgba(255,255,255,0.55),rgba(255,255,255,0)_55%)] tw-shadow-[inset_0_1px_1px_rgba(255,255,255,0.85),inset_0_-2px_6px_rgba(8,47,73,0.3)]" />
            <LuPlay aria-hidden="true" className="tw-relative tw-h-4 tw-w-4" />
            <span className="tw-relative tw-drop-shadow-[0_1px_1px_rgba(8,47,73,0.35)]">{canResume ? label('resume') : label('watch')}</span>
          </button>
          {canResume && (
            <button type="button" onClick={() => onOpen(null)} className="tw-inline-flex tw-items-center tw-justify-center tw-gap-1.5 tw-rounded-full tw-bg-transparent tw-px-3 tw-py-1.5 tw-text-xs tw-font-semibold tw-text-slate-600 hover:tw-bg-slate-900/[0.05] hover:tw-text-slate-900 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50 dark:tw-text-slate-300 dark:hover:tw-bg-white/10 dark:hover:tw-text-white">
              <LuRotateCcw aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
              {label('restart')}
            </button>
          )}
          {done > 0 && (
            <div className="tw-px-1">
              <p className="tw-flex tw-items-center tw-justify-between tw-text-[11px] tw-font-semibold tw-text-slate-500 dark:tw-text-slate-400">
                {done >= TOPIC_KEYS.length ? label('allWatched') : label('watched', { done, total: TOPIC_KEYS.length })}
              </p>
              <span className="tw-mt-1 tw-block tw-h-1.5 tw-overflow-hidden tw-rounded-full tw-bg-slate-200/80 dark:tw-bg-white/10">
                <span className="tw-block tw-h-full tw-rounded-full tw-bg-gradient-to-r tw-from-teal-400 tw-to-emerald-500" style={{ width: `${(done / TOPIC_KEYS.length) * 100}%` }} />
              </span>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

UserGuideCard.propTypes = { onOpen: PropTypes.func.isRequired };

export default UserGuideCard;
